// src/pages/Roadmap/StepDetail.jsx
// STATUS: active | UPDATED: 2026-10-19
// LOG:
//   2026-10-19  A demonstration video slot at the top of Learn, Word Tracks and
//               Practice (StepVideo, src/content/stepVideos.js). Empty today, so
//               it renders nothing until Jazz's footage is cut and listed there.
//   2026-08-29  Ask for a sign-off after passing. Passing the check proves the
//               person knows the step; the sign-off is a manager confirming
//               they can do it in front of a customer, which is the difference
//               between training and a trained floor.
//   2026-08-28  The check is graded by the server. The browser no longer knows
//               any correct answers, so it cannot mark its own homework and
//               does not try: it submits what was picked and renders the
//               verdict. See netlify/functions/grade-check.js and
//               src/lib/checkApi.js for why both leaks had to close together.
//   2026-08-02  Header standardized; Phase 1 checkpoint committed (e765830).
//   2026-08-01  New: Learn / Word Tracks / Practice / Check tabs, pass gate, commitment flow
// NEXT: Manager sign-off entry point in Phase 2
// One step of the Roadmap. Four movements matching the J13 learning loop:
// Learn, Word Tracks, Practice, Check. The knowledge check gates progression
// (see passMarkFor) and finishes with the next-customer commitment, which
// is the whole point: real application, not video completion.

import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  Box, Flex, HStack, Stack, VStack, Text, Button, Icon, Radio, RadioGroup, Textarea,
  Alert, AlertIcon, useToast, usePrefersReducedMotion,
} from '@chakra-ui/react'
import { ArrowLeft, ArrowRight, Check, MessageCircle, Quote, Sparkles } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import { getStepBySlug, STEPS } from '../../content/steps'
import { getProgress, startStep, recordAttempt, addCommitment, passMarkFor } from '../../lib/progressStore'
import { gradeCheck } from '../../lib/checkApi'
import { requestSignoff } from '../../lib/signoffs'
import HandoffLog from './HandoffLog'
import PeerTakes from './PeerTakes'
import StepVideo from './StepVideo'


function SectionCard({ children, ...rest }) {
  return (
    <Box bg="card" border="1px solid" borderColor="line" borderRadius="card" p={5} {...rest}>
      {children}
    </Box>
  )
}

function LearnTab({ step }) {
  return (
    <VStack align="stretch" spacing={4}>
      <StepVideo slug={step.slug} section="learn" />
      <SectionCard>
        <Text fontSize="mono-xs" fontFamily="mono" textTransform="uppercase" letterSpacing="0.08em" color="inkDim" mb={2}>
          Purpose
        </Text>
        <Text fontSize="body-lg" lineHeight={1.7} maxW="measure">
          {step.purpose}
        </Text>
      </SectionCard>
      {step.learn.map((section) => (
        <SectionCard key={section.heading}>
          <Text fontWeight={600} mb={2} letterSpacing="-0.01em">
            {section.heading}
          </Text>
          <Text fontSize="body" color="inkMuted" lineHeight={1.75} whiteSpace="pre-wrap" maxW="measure">
            {section.body}
          </Text>
        </SectionCard>
      ))}
    </VStack>
  )
}

function WordTracksTab({ step }) {
  return (
    <VStack align="stretch" spacing={3}>
      <Text fontSize="body-sm" color="inkMuted" lineHeight={1.6}>
        These are the approved J13 tracks. Personalize the words, never the sequence or intent.
      </Text>
      <StepVideo slug={step.slug} section="words" />
      {step.wordTracks.map((track, i) => (
        <SectionCard key={i} position="relative">
          <Icon as={Quote} boxSize={4} color="accent.500" mb={2} />
          <Text fontSize="body-lg" lineHeight={1.7} fontStyle="italic" mb={2}>
            {track.text}
          </Text>
          <Text fontSize="mono-xs" fontFamily="mono" textTransform="uppercase" letterSpacing="0.08em" color="inkDim">
            {track.label}
          </Text>
        </SectionCard>
      ))}
    </VStack>
  )
}

function PracticeTab({ step, onCoach, userId, dealershipId }) {
  return (
    <VStack align="stretch" spacing={4}>
      <StepVideo slug={step.slug} section="practice" />
      {step.practice.map((lab, i) => (
        <SectionCard key={i}>
          <Text fontWeight={600} mb={2} letterSpacing="-0.01em">
            {lab.title}
          </Text>
          <Text fontSize="body" color="inkMuted" lineHeight={1.75} mb={4} whiteSpace="pre-wrap">
            {lab.instructions}
          </Text>
          <Button size="sm" leftIcon={<MessageCircle size={15} />} onClick={onCoach}>
            Run this with the Coach
          </Button>
        </SectionCard>
      ))}
      {/* Only renders on 3, 7 and 11. HandoffLog returns null everywhere else,
          so there is no condition to keep in sync here. */}
      <SectionCard>
        <HandoffLog stepNumber={step.number} userId={userId} dealershipId={dealershipId} />
        {/* Peer teaching sits under the drills on purpose. You record how you do
            a step after you have practised it, not instead of practising it. */}
        <PeerTakes stepNumber={step.number} userId={userId} dealershipId={dealershipId} />
      </SectionCard>
    </VStack>
  )
}

function CheckTab({ step, userId, onPassed }) {
  const questions = step.knowledgeCheck
  const [answers, setAnswers] = useState({})
  const [result, setResult] = useState(null)
  const [grading, setGrading] = useState(false)
  const [error, setError] = useState(null)
  const toast = useToast()

  const allAnswered = questions.every((_, i) => answers[i] != null)

  const submit = async () => {
    if (grading) return
    setError(null)
    setGrading(true)

    const res = await gradeCheck(step.number, answers)

    if (!res.ok) {
      // The answers stay on screen. Losing somebody's four selections and then
      // telling them to try again is asking them to do it twice.
      setGrading(false)
      setError(res.error)
      return
    }

    const { score, total, passed, wrong } = res

    // grade-check already wrote the attempt and the step progress with the
    // service role, so this updates local state only. `graded` carries the
    // server's verdict rather than recomputing it from answers the browser
    // does not have.
    recordAttempt(userId, step.number, score, total, answers, { push: false, graded: passed })

    setGrading(false)
    setResult({ score, total, passed, wrong: wrong || [] })

    if (passed) {
      onPassed({ score, total })
    } else {
      toast({
        title: `${score} of ${total}. You need ${passMarkFor(total)} to pass. Review and go again.`,
        status: 'warning',
        duration: 4000,
      })
    }
  }

  if (result?.passed) return null

  return (
    <VStack align="stretch" spacing={4}>
      <Text fontSize="body-sm" color="inkMuted">
        {questions.length} questions. {passMarkFor(questions.length)} correct passes the step.
      </Text>
      {questions.map((q, i) => (
        <SectionCard key={i}>
          <Text fontWeight={600} mb={3} lineHeight={1.5} letterSpacing="-0.01em">
            {i + 1}. {q.question}
          </Text>
          <RadioGroup value={answers[i] != null ? String(answers[i]) : ''} onChange={(v) => setAnswers((a) => ({ ...a, [i]: Number(v) }))}>
            <VStack align="stretch" spacing={2}>
              {q.options.map((opt, j) => {
                // Marks the answer they chose on a question the server said was
                // wrong. It cannot mark the correct option, because the browser
                // is not told what it is, which is the entire point.
                const wrong = result && result.wrong.includes(i) && Number(answers[i]) === j
                return (
                  <HStack
                    key={j}
                    as="label"
                    border="1px solid"
                    borderColor={wrong ? 'danger' : Number(answers[i]) === j ? 'ink' : 'line'}
                    borderRadius="input"
                    px={3}
                    py={2.5}
                    cursor="pointer"
                    transition="border-color 120ms"
                    _hover={{ borderColor: 'inkMuted' }}
                  >
                    <Radio value={String(j)} colorScheme="brand" />
                    <Text fontSize="body" lineHeight={1.5}>
                      {opt}
                    </Text>
                  </HStack>
                )
              })}
            </VStack>
          </RadioGroup>
        </SectionCard>
      ))}
      {error && (
        <Alert status="error" borderRadius="md" fontSize="sm">
          <AlertIcon />
          {error}
        </Alert>
      )}
      <Button
        size="lg"
        isDisabled={!allAnswered || grading}
        isLoading={grading}
        loadingText="Checking"
        onClick={submit}
      >
        {result ? 'Try again' : 'Submit check'}
      </Button>
    </VStack>
  )
}

// The success moment. This is the emotional peak of the whole loop: the point
// where somebody has just proved something and decides whether this is worth
// opening again tomorrow. It used to be a small bordered card that said "Step 1
// passed", which was the quietest screen in the app at the loudest moment.
//
// It is now a full-contrast ink block, which DESIGN.md reserves for the moments
// that earn the disruption. Passing a step earns it.
//
// Deliberately not celebratory in the usual way. No confetti, no springs,
// nothing bouncing. Jazz's audience is a forty five year old F&I manager and the
// style guide bans that register. The weight comes from contrast and type, the
// same way it does in his printed materials.
//
// Three things it has to do, in order:
//   1. State plainly what was proved, with the score, because a real number is
//      the difference between recognition and flattery.
//   2. Make the commitment the hero rather than a footnote. The commitment is
//      the bridge from training to the floor and it is the only part of this
//      screen that changes what happens with a real customer.
//   3. Show the roadmap moving. Thirteen marks, filled as far as they have got.
// Passing the check is knowing it. Sign-off is doing it in front of somebody.
// Offered here rather than on a separate screen because this is the one moment
// the person is already thinking about the step they just proved.
function AskForSignoff({ step, userId }) {
  const [state, setState] = useState('idle')
  const [error, setError] = useState(null)

  const ask = async () => {
    if (state === 'sending') return
    setError(null)
    setState('sending')
    const res = await requestSignoff(userId, step.number)
    if (!res.ok) {
      setError(res.error || 'Could not send that. Try again in a moment.')
      setState('idle')
      return
    }
    setState('sent')
  }

  if (state === 'sent') {
    return (
      <Text fontSize="body-sm" color="onInkMuted" mt={5} maxW="measure">
        Your manager has been asked to sign off Step {step.number}. It stays on their list until
        they have watched you run it.
      </Text>
    )
  }

  return (
    <Box mt={5}>
      {error && (
        <Text fontSize="body-sm" color="accent.500" mb={2} maxW="measure">
          {error}
        </Text>
      )}
      <Button
        variant="ghost"
        color="onInkSubtle"
        _hover={{ bg: 'onInkFill', color: 'onInk' }}
        minH="40px"
        px={4}
        isLoading={state === 'sending'}
        loadingText="Sending"
        onClick={ask}
      >
        Ask my manager to sign this off
      </Button>
    </Box>
  )
}

function CommitmentGate({ step, userId, nextStep, result, completedCount, totalCount }) {
  const [text, setText] = useState('')
  const [saved, setSaved] = useState(false)
  const navigate = useNavigate()
  const reduceMotion = usePrefersReducedMotion()
  // Getting every question right should not look identical to scraping a pass.
  const perfect = !!result?.total && result.score === result.total

  const save = () => {
    if (text.trim()) addCommitment(userId, step.number, text.trim())
    setSaved(true)
  }

  return (
    <Box
      bg="ink"
      color="onInk"
      borderRadius="cardLg"
      p={{ base: 6, md: 8 }}
      sx={
        reduceMotion
          ? undefined
          : {
              animation: 'j13rise 420ms cubic-bezier(0.16, 1, 0.3, 1) both',
              '@keyframes j13rise': {
                from: { opacity: 0, transform: 'translateY(10px)' },
                to: { opacity: 1, transform: 'none' },
              },
            }
      }
    >
      <HStack spacing={3} mb={5}>
        <Flex
          boxSize="30px"
          borderRadius="full"
          bg={perfect ? 'accent.500' : 'onInkFill'}
          color={perfect ? 'onInk' : undefined}
          align="center"
          justify="center"
          flexShrink={0}
          sx={
            reduceMotion || !perfect
              ? undefined
              : {
                  animation: 'j13pop 520ms cubic-bezier(0.34, 1.56, 0.64, 1) 120ms both',
                  '@keyframes j13pop': {
                    from: { transform: 'scale(0.4)', opacity: 0 },
                    to: { transform: 'scale(1)', opacity: 1 },
                  },
                }
          }
        >
          <Check size={15} strokeWidth={3} />
        </Flex>
        <Text textStyle="eyebrow" color="accent.500">
          {perfect ? 'Perfect score' : `Step ${step.number} of ${totalCount} cleared`}
        </Text>
      </HStack>

      {/* The score was small subtle mono, which is why Jazz read "4 of 4" as
          "404" and said finishing a step did not feel like anything. A person
          who got every question right should be told so in words, at a size
          they can read across a desk. */}
      {result?.total ? (
        <>
          <Text fontSize="display-md" fontWeight={600} letterSpacing="-0.02em" lineHeight={1.1}>
            {perfect ? 'Every one right.' : `${result.score} out of ${result.total}.`}
          </Text>
          <Text fontSize="display-md" fontWeight={600} letterSpacing="-0.02em" mb={2} color="onInkMuted">
            {step.title}.
          </Text>
          <Text fontSize="body" color="onInkMuted" mb={5} maxW="measure">
            {perfect
              ? `You answered all ${result.total} correctly. That is Step ${step.number} of ${totalCount} done properly, not scraped through.`
              : `That clears Step ${step.number} of ${totalCount}.`}
          </Text>
        </>
      ) : (
        <>
          <Text fontSize="display-md" fontWeight={600} letterSpacing="-0.02em" mb={2}>
            {step.title}.
          </Text>
          <Box mb={5} />
        </>
      )}

      {/* the roadmap moving, thirteen marks */}
      <HStack spacing={1.5} mb={7} aria-label={`${completedCount} of ${totalCount} steps complete`}>
        {Array.from({ length: totalCount }, (_, i) => (
          <Box
            key={i}
            h="3px"
            flex={1}
            borderRadius="full"
            bg={i < completedCount ? 'onInkStrong' : 'onInkFill'}
            transition={reduceMotion ? undefined : 'background 300ms ease'}
          />
        ))}
      </HStack>

      {!saved ? (
        <>
          <Text fontSize="lead" fontWeight={600} mb={1.5} letterSpacing="-0.01em">
            Now make it real.
          </Text>
          <Text fontSize="body" color="onInkMuted" lineHeight={1.65} mb={4} maxW="measure">
            {step.commitment || 'On my next customer, I will apply this specific behavior.'}
          </Text>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="One specific thing you will do differently on your next customer"
            rows={3}
            mb={4}
            bg="onInkFill"
            border="1px solid"
            borderColor="onInkFill2"
            color="onInk"
            _placeholder={{ color: 'onInkSubtle' }}
            _hover={{ borderColor: 'onInkSubtle' }}
            _focusVisible={{ borderColor: 'onInkStrong', boxShadow: 'none' }}
            maxW="measure"
          />
          <Stack direction={{ base: 'column', sm: 'row' }} spacing={2} align={{ base: 'stretch', sm: 'center' }}>
            <Button
              onClick={save}
              isDisabled={!text.trim()}
              bg="onInk"
              color="ink"
              _hover={{ bg: 'onInkStrong' }}
              _active={{ bg: 'onInkStrong' }}
            >
              Commit to it
            </Button>
            <Button variant="ghost" color="onInkSubtle" _hover={{ bg: 'onInkFill', color: 'onInk' }} onClick={() => setSaved(true)}>
              Not this time
            </Button>
          </Stack>
        </>
      ) : (
        <>
          {text.trim() ? (
            <>
              <Text textStyle="eyebrow" color="accent.500" mb={2}>
                Your commitment
              </Text>
              <Text fontSize="lead" lineHeight={1.6} mb={5} maxW="measure">
                {text.trim()}
              </Text>
              <Text fontSize="body-sm" color="onInkMuted" mb={6} maxW="measure">
                It will be waiting on your Today screen. Come back and say how it went.
              </Text>
            </>
          ) : (
            <Text fontSize="body" color="onInkMuted" mb={6} maxW="measure">
              No commitment this time. The step still counts.
            </Text>
          )}
          {/* The commitment says what they will do with a real customer. This
              is where they get to try it first, on this step, while it is still
              in their head. Jazz's feedback was that practice is what he
              engaged with most, and until now it was somewhere else entirely. */}
          <Button
            onClick={() => navigate(`/coach/?practice=${step.number}`)}
            leftIcon={<Sparkles size={16} />}
            variant="outline"
            borderColor="onInkFill2"
            color="onInk"
            _hover={{ bg: 'onInkFill' }}
            mb={5}
            w={{ base: 'full', sm: 'auto' }}
          >
            Run it with the coach first
          </Button>

          <AskForSignoff step={step} userId={userId} />
          <Stack direction={{ base: 'column', sm: 'row' }} spacing={2} align={{ base: 'stretch', sm: 'center' }}>
            {nextStep ? (
              <Button
                rightIcon={<ArrowRight size={16} />}
                onClick={() => navigate(`/courses/roadmap/${nextStep.slug}/`)}
                bg="onInk"
                color="ink"
                _hover={{ bg: 'onInkStrong' }}
              >
                Next: {nextStep.title}
              </Button>
            ) : (
              <Button
                onClick={() => navigate('/courses/roadmap/')}
                bg="onInk"
                color="ink"
                _hover={{ bg: 'onInkStrong' }}
              >
                That is all thirteen
              </Button>
            )}
            <Button variant="ghost" color="onInkSubtle" _hover={{ bg: 'onInkFill', color: 'onInk' }} onClick={() => navigate('/courses/roadmap/')}>
              Back to Roadmap
            </Button>
          </Stack>
        </>
      )}
    </Box>
  )
}

export default function StepDetail() {
  const { slug } = useParams()
  const { user, profile } = useAuth()
  const navigate = useNavigate()
  const step = getStepBySlug(slug)
  // ?tab= lets the coach send somebody to the right half of a step rather than
  // to its front door. See src/lib/coachLinks.js for the tokens that produce it.
  const [searchParams] = useSearchParams()
  const TABS = { learn: 0, words: 1, practice: 2, check: 3 }
  const [tab, setTab] = useState(TABS[searchParams.get('tab')] ?? 0)
  // null until the check is passed in this session, then { score, total }.
  const [justPassed, setJustPassed] = useState(null)

  const progress = useMemo(() => getProgress(user?.id), [user?.id])
  const stepState = step ? progress.steps.find((s) => s.number === step.number) : null

  useEffect(() => {
    if (step && stepState?.unlocked) startStep(user?.id, step.number)
    window.scrollTo(0, 0)
  }, [slug]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!step) {
    navigate('/courses/roadmap/', { replace: true })
    return null
  }

  if (stepState && !stepState.unlocked) {
    navigate('/courses/roadmap/', { replace: true })
    return null
  }

  const alreadyPassed = stepState?.passed && !justPassed
  const nextStep = STEPS.find((s) => s.number === step.number + 1) || null

  // Read fresh rather than from the mount-time memo: recordAttempt has already
  // run by the time this renders, so the memo is one step behind and the rail
  // would show the roadmap not moving on the exact screen that exists to show
  // it moving.
  const passedCount = justPassed
    ? getProgress(user?.id).completedCount
    : progress.completedCount

  return (
    <Box maxW="sheet">
      <HStack mb={5} spacing={3}>
        <Button variant="ghost" size="sm" leftIcon={<ArrowLeft size={15} />} onClick={() => navigate('/courses/roadmap/')} px={2}>
          Roadmap
        </Button>
      </HStack>

      <Box mb={6}>
        <Text fontSize="mono-xs" fontFamily="mono" textTransform="uppercase" letterSpacing="0.1em" color="accent.500" mb={1}>
          Step {step.number} of 13
        </Text>
        <Text fontSize="display-lg" fontWeight={600} letterSpacing="-0.02em" lineHeight={1.15}>
          {step.title}
        </Text>
      </Box>

      {(justPassed || alreadyPassed) && (
        <Box mb={6}>
          <CommitmentGate
            step={step}
            userId={user?.id}
            nextStep={nextStep}
            result={justPassed}
            completedCount={passedCount}
            totalCount={STEPS.length}
          />
        </Box>
      )}

      {/* segmented tabs, horizontally scrollable on small screens */}
      <HStack
        spacing={1}
        mb={6}
        bg="surface"
        borderRadius="pill"
        p={1}
        overflowX="auto"
        css={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}
      >
        {TABS.map((label, i) => (
          <Button
            key={label}
            size="sm"
            flexShrink={0}
            flex={{ base: 'none', md: 1 }}
            variant="unstyled"
            // See the coach composer: unstyled loses the theme's _placeholder
            // and lands on Chakra's whiteAlpha.400 at 2.2 contrast.
            _placeholder={{ color: 'inkDim' }}
            display="flex"
            px={4}
            h="34px"
            borderRadius="pill"
            fontSize="body-sm"
            fontWeight={tab === i ? 600 : 500}
            bg={tab === i ? 'card' : 'transparent'}
            color={tab === i ? 'ink' : 'inkMuted'}
            boxShadow={tab === i ? 'card' : 'none'}
            onClick={() => setTab(i)}
            transition="all 150ms"
          >
            {label}
          </Button>
        ))}
      </HStack>

      {tab === 0 && <LearnTab step={step} />}
      {tab === 1 && <WordTracksTab step={step} />}
      {tab === 2 && (
        <PracticeTab
          step={step}
          onCoach={() => navigate('/coach/')}
          userId={user?.id}
          dealershipId={profile?.dealership_id}
        />
      )}
      {tab === 3 && (
        <CheckTab
          step={step}
          userId={user?.id}
          onPassed={(scored) => {
            setJustPassed(scored)
            window.scrollTo({ top: 0, behavior: 'smooth' })
          }}
        />
      )}
    </Box>
  )
}
